import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Card as MuiCard,
  TextField,
  Button,
  InputBase,
  Grid,
  List,
  ListItem,
  ListItemText,
  Collapse,
  Popover,
  IconButton,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from "@material-ui/core";
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Link as LinkIcon,
  FormatListBulleted,
  FormatListNumbered,
  Title as TitleIcon,
} from "@material-ui/icons";
import { useParams, useHistory } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store";
import { getProgramById } from "../../redux/actions/programAction";
import useStyles from "../../containers/Auth/Onboarding/style";
import backIcon from "../../assets/icons/backIcon.svg";
import appLogo from "../../assets/logo.png";
import { iconNames } from "../../constants/materialIconNames";
import tulips from "../../assets/themeImages/tulips.jpg";
import sunset from "../../assets/themeImages/sunset.png";
import ocean from "../../assets/themeImages/ocean.png";
import medical from "../../assets/themeImages/medical.png";
import golf from "../../assets/themeImages/golf.png";
import forrest from "../../assets/themeImages/forrest.png";
import { assetUrl } from "../../util/assetUrl";
import { INSTANCE } from "../../api/endpoint";
import Cropper from "react-easy-crop";
import NotificationPolicyEditor from "../../components/NotificationPolicyEditor";
import AdminChatroomManager from "./AdminChatroomManager";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9090";
const ASSET_BASE = process.env.REACT_APP_ASSET_BASE_URL || API_BASE;

// Preset Themes
const PRESET_THEMES = [
  { id: "default", name: "Default Blue", mainColor: "#1976d2", secondaryColor: "#ffffff", headerImage: "", fontStyle: "Arial" },
  { id: "dark", name: "Dark Mode", mainColor: "#bb86fc", secondaryColor: "#121212", headerImage: "", fontStyle: "Arial" },
  { id: "sports", name: "Sports", mainColor: "#4caf50", secondaryColor: "#e8f5e8", headerImage: golf, fontStyle: "Arial" },
  { id: "red-flower", name: "Red Flower", mainColor: "#e91e63", secondaryColor: "#fce4ec", headerImage: tulips, fontStyle: "Arial" },
  { id: "ocean", name: "Ocean Blue", mainColor: "#0277bd", secondaryColor: "#e1f5fe", headerImage: ocean, fontStyle: "Arial" },
  { id: "sunset", name: "Sunset Orange", mainColor: "#ff6f00", secondaryColor: "#fff3e0", headerImage: sunset, fontStyle: "Arial" },
  { id: "forest", name: "Forest Green", mainColor: "#2e7d32", secondaryColor: "#e8f5e8", headerImage: forrest, fontStyle: "Arial" },
  { id: "purple", name: "Royal Purple", mainColor: "#7b1fa2", secondaryColor: "#f3e5f5", headerImage: "", fontStyle: "Arial" },
  { id: "corporate", name: "Corporate Gray", mainColor: "#37474f", secondaryColor: "#eceff1", headerImage: "", fontStyle: "Arial" },
  { id: "medical", name: "Medical", mainColor: "#00796b", secondaryColor: "#e0f2f1", headerImage: medical, fontStyle: "Arial" },
];

// Helper: invert hex color
function invertColor(hex: string) {
  let col = (hex || "#ffffff").replace(/^#/, "");
  if (col.length === 3) col = col.split("").map((c) => c + c).join("");
  const num = parseInt(col || "ffffff", 16);
  const r = 255 - ((num >> 16) & 0xff);
  const g = 255 - ((num >> 8) & 0xff);
  const b = 255 - (num & 0xff);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

// detect & decode entity-encoded HTML
const looksEntityEncoded = (s: string) => !!s && /&lt;|&gt;|&amp;/.test(s) && !/<[a-zA-Z][\s\S]*>/.test(s);
const decodeEntities = (s: string) => {
  const ta = document.createElement("textarea");
  ta.innerHTML = s;
  return ta.value;
};

// Resolve stored path using assetUrl (prevents double-prefix bugs)
const resolveHeaderUrl = (p: string): string => {
  if (!p) return "";
  if (/^(blob:|data:|https?:\/\/)/i.test(p)) return p;
  return assetUrl(p);
};

export default function AdminChannelEditor() {
  const history = useHistory();
  const classes = useStyles();
  const dispatch = useDispatch();
  const isMounted = useRef(true);

  const [channelName, setChannelName] = useState("");
  const [description, setDescription] = useState("");
  const [publish, setPublish] = useState(true);
  const [isPrivate, setIsPrivate] = useState(false);
  const [showToCommunity, setShowToCommunity] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string>("");
  const [selectedThemeId, setSelectedThemeId] = useState("default");
  const [channelStyle, setChannelStyle] = useState({
    mainColor: "#1976d2",
    secondaryColor: "#ffffff",
    headerImage: "",
    fontStyle: "Arial",
  });

  // cache-buster for header & logo images
  const [headerBust, setHeaderBust] = useState<number>(0);
  const [logoBust, setLogoBust] = useState<number>(0);
  const headerBlobUrlRef = useRef<string | null>(null);

  // Header upload UI state
  const headerFileInputRef = useRef<HTMLInputElement | null>(null);
  const [headerUploading, setHeaderUploading] = useState(false);
  const [headerUploadMsg, setHeaderUploadMsg] = useState<string>("");

  // Manual crop modal state
  const [cropOpen, setCropOpen] = useState(false);
  const [cropImgSrc, setCropImgSrc] = useState<string>("");
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  // THEME TABS: 0 = Default, 1 = Custom
  const [themeTab, setThemeTab] = useState<number>(0);
  const getThemeById = (id: string) => PRESET_THEMES.find((t) => t.id === id);
  const isPresetId = (id: string) => !!getThemeById(id);

  // Rich-text "about"
  const [aboutHtml, setAboutHtml] = useState<string>("");
  const [aboutOpen, setAboutOpen] = useState<boolean>(false);
  const [aboutDraft, setAboutDraft] = useState<string>("");
  const aboutEditorRef = useRef<HTMLDivElement | null>(null);

  const [modsOpen, setModsOpen] = useState(false);
  const [notificationPolicyOpen, setNotificationPolicyOpen] = useState(false);
  const [chatroomsOpen, setChatroomsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [iconSearch, setIconSearch] = useState("");
  const [showAllIcons, setShowAllIcons] = useState(false);
  const [moderators, setModerators] = useState<any[]>([]);

  const { programId: urlProgramId, communityIdentifier, channelIdentifier } =
    useParams<{ programId?: string; communityIdentifier: string; channelIdentifier: string }>();

  const reduxProgram = useSelector((state: RootState) => state.programReducer.singleProgram);
  const programId = urlProgramId || reduxProgram?._id;

  const community = useMemo(
    () => reduxProgram?.communities?.find((c: any) => c.communityIdentifier === communityIdentifier),
    [reduxProgram, communityIdentifier]
  );
  const channel = useMemo(
    () => community?.channels?.find((ch: any) => ch.channelIdentifier === channelIdentifier),
    [community, channelIdentifier]
  );

  const invertedSecondary = useMemo(
    () => invertColor(channelStyle.secondaryColor),
    [channelStyle.secondaryColor]
  );

  // usable URL whenever headerImage or bust changes
  const headerImageUrl = useMemo(() => {
    const u = resolveHeaderUrl(channelStyle.headerImage);
    if (!u) return "";
    return headerBust ? `${u}${u.includes("?") ? "&" : "?"}v=${headerBust}` : u;
  }, [channelStyle.headerImage, headerBust]);

  // resolve community logo with cache-bust; fallback to app logo
  const communityLogoRaw = community?.communityStyle?.logo || "";
  useEffect(() => setLogoBust(Date.now()), [communityLogoRaw, communityIdentifier]);
  const communityLogoUrl = useMemo(() => {
    const u = resolveHeaderUrl(communityLogoRaw);
    if (!u) return "";
    return logoBust ? `${u}${u.includes("?") ? "&" : "?"}v=${logoBust}` : u;
  }, [communityLogoRaw, logoBust]);

  useEffect(() => () => {
    isMounted.current = false;
    if (headerBlobUrlRef.current) {
      URL.revokeObjectURL(headerBlobUrlRef.current);
      headerBlobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (programId && !reduxProgram) dispatch(getProgramById(programId));
  }, [programId, reduxProgram, dispatch]);

  useEffect(() => {
    if (!channel) return;

    setChannelName(channel.channelName || "");
    setDescription(channel.channelDetails?.description || "");
    setShowToCommunity(!!channel.showToCommunity);
    setSelectedIcon(channel.channelPostingSettings?.defaultIcon || "");
    setPublish(channel.publish ?? true);
    setIsPrivate(channel.channelPrivacySettings?.visibility === "private");

    const currentStyle = {
      mainColor: channel.channelStyle?.mainColor || "#1976d2",
      secondaryColor: channel.channelStyle?.secondaryColor || "#ffffff",
      headerImage: channel.channelStyle?.headerImage || "",
      fontStyle: channel.channelStyle?.fontStyle || "Arial",
    };
    setChannelStyle(currentStyle);
    setHeaderBust(Date.now()); // fresh copy

    const matchingTheme = PRESET_THEMES.find(
      (theme) =>
        theme.mainColor === currentStyle.mainColor &&
        theme.secondaryColor === currentStyle.secondaryColor &&
        theme.headerImage === currentStyle.headerImage
    );

    if (matchingTheme) {
      setSelectedThemeId(matchingTheme.id);
      setThemeTab(0);
    } else {
      setSelectedThemeId("custom");
      setThemeTab(1);
    }

    const rawAbout: string = channel.about ?? channel.channelDetails?.about ?? "";
    const normalized = looksEntityEncoded(rawAbout) ? decodeEntities(rawAbout) : rawAbout;
    setAboutHtml(normalized);
  }, [channel]);

  // sync draft only when modal opens
  useEffect(() => {
    if (aboutOpen) {
      setAboutDraft(aboutHtml || "");
      requestAnimationFrame(() => {
        if (aboutEditorRef.current) {
          aboutEditorRef.current.innerHTML = aboutHtml || "";
          const sel = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(aboutEditorRef.current);
          range.collapse(false);
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      });
    }
  }, [aboutOpen, aboutHtml]);

  // Fetch moderators and administrators
  useEffect(() => {
    if (!programId || !communityIdentifier) return;

    const fetchModerators = async () => {
      try {
        const { data } = await INSTANCE.get(
          `/v1/admin/community/${communityIdentifier}/members`
        );

        if (Array.isArray(data)) {
          // Filter for users with moderator, communityAdmin, or admin roles
          const modsAndAdmins = data.filter((member: any) => {
            const role = member.role || member.userType;
            return role === 'moderator' || role === 'communityAdmin' || role === 'admin';
          });
          setModerators(modsAndAdmins);
        }
      } catch (error) {
        console.error('Failed to fetch moderators:', error);
        setModerators([]);
      }
    };

    fetchModerators();
  }, [programId, communityIdentifier]);

  const filteredIcons = useMemo(
    () => iconNames.filter((n) => n.toLowerCase().includes(iconSearch.toLowerCase())),
    [iconSearch]
  );
  const iconsToDisplay = useMemo(() => {
    if (showAllIcons) return filteredIcons;
    if (selectedIcon) return [selectedIcon, ...filteredIcons.filter((i) => i !== selectedIcon).slice(0, 11)];
    return filteredIcons.slice(0, 12);
  }, [filteredIcons, showAllIcons, selectedIcon]);

  const openIconPicker = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const closeIconPicker = () => {
    setAnchorEl(null);
    setIconSearch("");
    setShowAllIcons(false);
  };
  const popoverOpen = Boolean(anchorEl);

  const handleThemeSelect = (themeId: string) => {
    const theme = PRESET_THEMES.find((t) => t.id === themeId);
    if (theme) {
      setSelectedThemeId(themeId);
      setThemeTab(0);
      setChannelStyle({
        mainColor: theme.mainColor,
        secondaryColor: theme.secondaryColor,
        headerImage: theme.headerImage,
        fontStyle: theme.fontStyle,
      });
      setHeaderBust(0);
    }
  };

  // ---------- IMAGE UPLOAD HELPERS ----------

  // Convert user's crop selection to a banner blob (≈3.5:1)
  const getCroppedBlob = async (imageSrc: string, cropPx: any): Promise<Blob> => {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = "anonymous";
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = imageSrc;
    });
    const canvas = document.createElement("canvas");
    canvas.width = 1400;
    canvas.height = Math.round(canvas.width / 3.5);
    const ctx = canvas.getContext("2d")!;

    const temp = document.createElement("canvas");
    temp.width = cropPx.width;
    temp.height = cropPx.height;
    const tctx = temp.getContext("2d")!;
    tctx.drawImage(
      img,
      cropPx.x, cropPx.y, cropPx.width, cropPx.height,
      0, 0, cropPx.width, cropPx.height
    );
    ctx.drawImage(temp, 0, 0, canvas.width, canvas.height);

    return await new Promise<Blob>((resolve) =>
      canvas.toBlob((b) => resolve(b || new Blob()), "image/jpeg", 0.92)
    );
  };

  // Upload blob/file -> set headerImage + cache-bust **(CHANNEL route)**
  const uploadHeaderFile = async (blobOrFile: Blob, filename = "header.jpg") => {
    if (!programId || !communityIdentifier || !channelIdentifier) {
      setHeaderUploadMsg("Missing program / community / channel identifiers.");
      return;
    }
    try {
      setHeaderUploading(true);
      setHeaderUploadMsg("");

      const fd = new FormData();
      fd.append("programId", String(programId));
      fd.append("communityIdentifier", String(communityIdentifier));
      fd.append("channelIdentifier", String(channelIdentifier));
      fd.append("file", blobOrFile, filename);

      // derive teamId used by your /:teamId/:programId upload route
      const teamId =
        (reduxProgram as any)?.teamId ||
        (reduxProgram as any)?.team?._id ||
        "default";

      // CHANNEL header endpoint (maps to controller.uploadLocalChannelHeader)
      const { data } = await INSTANCE.post(`/v1/users/${teamId}/${programId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const url: string = data?.data?.url || data?.url || "";
      if (!url) throw new Error("No URL returned from upload");

      setChannelStyle((s) => ({ ...s, headerImage: url }));
      setHeaderBust(Date.now());
      setHeaderUploadMsg("Header image updated.");
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || "Upload failed. Please try again.";
      setHeaderUploadMsg(msg);
    } finally {
      setHeaderUploading(false);
    }
  };

  // Pick file → if SVG upload directly; else open crop modal
  const onHeaderFilePicked: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type === "image/svg+xml") {
      await uploadHeaderFile(file, "header.svg");
      if (headerFileInputRef.current) headerFileInputRef.current.value = "";
      return;
    }

    const fr = new FileReader();
    fr.onload = () => {
      setCropImgSrc(String(fr.result || ""));
      setCropOpen(true);
      setHeaderUploadMsg("");
    };
    fr.readAsDataURL(file);

    if (headerFileInputRef.current) headerFileInputRef.current.value = "";
  };

  // About editor helpers
  const exec = (cmd: string, val?: string) => {
    document.execCommand(cmd, false, val);
    aboutEditorRef.current?.focus();
  };
  const makeHeading = (level: 1 | 2) => {
    exec("formatBlock", level === 1 ? "H1" : "H2");
  };
  const insertLink = () => {
    const url = window.prompt("Enter URL");
    if (url) exec("createLink", url);
  };

  // Paste handler for About
  const onAboutPaste = async (e: React.ClipboardEvent<HTMLDivElement>) => {
    const dt = e.clipboardData;
    if (!dt) return;

    const items = Array.from(dt.items || []);
    const imgItem = items.find((it) => it.type.startsWith("image/"));
    if (imgItem) {
      e.preventDefault();
      const file = imgItem.getAsFile();
      if (file) {
        const url = URL.createObjectURL(file);
        document.execCommand("insertImage", false, url);
        setAboutDraft(aboutEditorRef.current?.innerHTML || "");
      }
      return;
    }

    const text = dt.getData("text/plain");
    if (text && /^(https?:|data:)/i.test(text) && /(\/[^?#]+\.(png|jpe?g|gif|webp|svg)(\?.*)?$)|^data:image\//i.test(text)) {
      e.preventDefault();
      document.execCommand("insertImage", false, text);
      setAboutDraft(aboutEditorRef.current?.innerHTML || "");
    }
  };

  // ---------- CROP MODAL ----------
  const CropModal = cropOpen ? (
    <div
      style={{
        position: "fixed",
        inset: 0 as any,
        background: "#000A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <div
        style={{
          width: "min(980px, 96vw)",
          height: "min(620px, 86vh)",
          background: channelStyle.secondaryColor,
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0,0,0,.4)",
        }}
      >
        {/* Header */}
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: 12,
            borderBottom: `1px solid ${invertColor(channelStyle.secondaryColor)}22`,
            background: `${invertColor(channelStyle.secondaryColor)}08`,
            color: invertColor(channelStyle.secondaryColor),
          }}
        >
          <Typography variant="subtitle1">Crop Header</Typography>
          <Box display="flex" gridGap={8 as any}>
            <Button onClick={() => setCropOpen(false)}>Cancel</Button>
            <Button
              color="primary"
              variant="contained"
              disabled={!croppedAreaPixels || headerUploading}
              onClick={async () => {
                try {
                  if (!cropImgSrc || !croppedAreaPixels) return;
                  const blob = await getCroppedBlob(cropImgSrc, croppedAreaPixels);
                  await uploadHeaderFile(blob, "header.jpg");
                  setCropOpen(false);
                } catch {
                  setHeaderUploadMsg("Failed to crop image.");
                }
              }}
            >
              {headerUploading ? "Saving…" : "Save Crop"}
            </Button>
          </Box>
        </Box>

        {/* Cropper */}
        <div style={{ position: "relative", flex: 1, background: "#111" }}>
          <Cropper
            image={cropImgSrc}
            crop={crop}
            zoom={zoom}
            aspect={3.5}
            onCropChange={(c) => setCrop(c)}
            onZoomChange={(z) => setZoom(z)}
            onCropComplete={(_, areaPixels) => setCroppedAreaPixels(areaPixels)}
            showGrid={false}
          />
        </div>

        {/* Zoom + status */}
        <Box
          p={2}
          style={{
            borderTop: `1px solid ${invertColor(channelStyle.secondaryColor)}22`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: invertColor(channelStyle.secondaryColor),
          }}
        >
          <Typography variant="caption" style={{ opacity: 0.8 }}>
            Zoom
          </Typography>
          <input
            type="range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ width: 240 }}
          />
          {!!headerUploadMsg && (
            <Typography variant="caption" style={{ marginLeft: "auto", opacity: 0.8 }}>
              {headerUploadMsg}
            </Typography>
          )}
        </Box>
      </div>
    </div>
  ) : null;

  // ---------- RENDER ----------
  if (!reduxProgram || !channel) {
    return (
      <Box
        style={{ backgroundColor: channelStyle.secondaryColor, color: invertedSecondary }}
        className={classes.container}
        textAlign="center"
        mt={10}
      >
        <Typography style={{ color: invertedSecondary }}>Loading channel data…</Typography>
      </Box>
    );
  }

  const handleSave = async () => {
    if (!programId || !channel || !community || !reduxProgram) return;

    setSaving(true);
    setSaveError("");

    const canonicalAbout = looksEntityEncoded(aboutDraft) ? decodeEntities(aboutDraft) : aboutDraft;

    const updatedChannel = {
      ...channel,
      channelName,
      showToCommunity,
      channelDetails: {
        ...(channel.channelDetails || {}),
        description,
        about: canonicalAbout || "",
      },
      publish,
      channelPrivacySettings: {
        ...(channel.channelPrivacySettings || {}),
        visibility: isPrivate ? "private" : "public",
      },
      channelStyle: {
        ...(channel.channelStyle || {}),
        mainColor: channelStyle.mainColor,
        secondaryColor: channelStyle.secondaryColor,
        headerImage: channelStyle.headerImage,
        fontStyle: channelStyle.fontStyle,
      },
      channelPostingSettings: {
        ...channel.channelPostingSettings,
        defaultIcon: selectedIcon,
        icons: [
          selectedIcon,
          ...(channel.channelPostingSettings?.icons?.filter((i: string) => i !== selectedIcon) || []),
        ],
      },
      about: canonicalAbout || "",
    };

    const updatedProgram = {
      ...reduxProgram,
      communities: reduxProgram.communities.map((c: any) =>
        c.communityIdentifier === communityIdentifier
          ? {
              ...c,
              channels: c.channels.map((ch: any) =>
                ch.channelIdentifier === channelIdentifier ? updatedChannel : ch
              ),
            }
          : c
      ),
    };

    // optimistic UI
    dispatch({ type: "GET_SINGLE_PROGRAM", payload: updatedProgram });
    dispatch({
      type: "SET_CHANNEL_THEME",
      channelId: channelIdentifier,
      payload: { ...channelStyle },
    });
    dispatch({ type: "CLEAR_SUBSCRIBE_CHANNELS" });

    try {
      await INSTANCE.put(`/v1/programs/${programId}`, updatedProgram);
      history.go(-1);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to save changes.";
      setSaveError(msg);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const AboutModal = aboutOpen ? (
    <div
      style={{
        position: "fixed",
        inset: 0 as any,
        background: "#000A",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <div
        style={{
          width: "min(900px, 96vw)",
          height: "min(640px, 86vh)",
          background: channelStyle.secondaryColor,
          borderRadius: 12,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0,0,0,.4)",
        }}
      >
        {/* Toolbar */}
        <Box
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            padding: 8,
            borderBottom: `1px solid ${invertColor(channelStyle.secondaryColor)}22`,
            background: `${invertColor(channelStyle.secondaryColor)}08`,
          }}
        >
          <IconButton onClick={() => exec("bold")}><FormatBold /></IconButton>
          <IconButton onClick={() => exec("italic")}><FormatItalic /></IconButton>
          <IconButton onClick={() => exec("underline")}><FormatUnderlined /></IconButton>
          <IconButton onClick={() => makeHeading(1)}><TitleIcon /></IconButton>
          <IconButton onClick={() => makeHeading(2)}><TitleIcon fontSize="small" /></IconButton>
          <IconButton onClick={insertLink}><LinkIcon /></IconButton>
          <Box flex={1} />
          <Button onClick={() => setAboutOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              const html = aboutEditorRef.current?.innerHTML || "";
              setAboutDraft(html);
              setAboutHtml(html);
              setAboutOpen(false);
            }}
          >
            Save
          </Button>
        </Box>

        <style>{`
          .about-editor-content img { max-width: 100%; height: auto; }
          .about-editor-content a { color: inherit; text-decoration: underline; }
        `}</style>

        <div
          ref={aboutEditorRef}
          className="about-editor-content"
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => setAboutDraft((e.currentTarget as HTMLDivElement).innerHTML || "")}
          style={{
            flex: 1,
            padding: 16,
            outline: "none",
            overflow: "auto",
            color: invertColor(channelStyle.secondaryColor),
            background: channelStyle.secondaryColor,
          }}
        />
      </div>
    </div>
  ) : null;

  return (
    <>
      {CropModal}
      {AboutModal}
      <div
        className={classes.container}
        style={{ backgroundColor: channelStyle.secondaryColor, color: invertedSecondary }}
      >
        <MuiCard
          className={classes.card}
          style={{
            backgroundColor: channelStyle.secondaryColor,
            border: `1px solid ${invertedSecondary}`,
            boxShadow: `0 8px 24px ${invertedSecondary}33`,
          }}
        >
          <Box
            className={classes.header}
            style={{ backgroundColor: channelStyle.mainColor, color: channelStyle.secondaryColor }}
          >
            <IconButton onClick={() => history.go(-1)}>
              <img src={backIcon} alt="Back" />
            </IconButton>
            <Box className={classes.logoWrapper}>
              <img
                src={communityLogoUrl || resolveHeaderUrl(appLogo)}
                alt="Logo"
                className={classes.logo}
              />
            </Box>
          </Box>

          <Typography component="h1" className={classes.onboardingTitle} style={{ color: channelStyle.mainColor }}>
            Edit Channel
          </Typography>
          <Typography component="h2" className={classes.onboardingHeading} style={{ color: invertedSecondary }}>
            {communityIdentifier} / {channelIdentifier}
          </Typography>

          {/* ICON SELECTOR */}
          <Box mt={2}>
            <Button
              variant="outlined"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              startIcon={
                <i className="material-icons" style={{ color: channelStyle.mainColor }}>
                  {selectedIcon || "note"}
                </i>
              }
              style={{ color: channelStyle.mainColor, borderColor: channelStyle.mainColor }}
            >
              Change Icon
            </Button>

            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={closeIconPicker}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Box
                p={2}
                maxWidth={420}
                style={{
                  backgroundColor: channelStyle.secondaryColor,
                  color: invertedSecondary,
                }}
              >
                {/* Search bar */}
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    border: `1px solid ${invertedSecondary}55`,
                    borderRadius: 8,
                    padding: 8,
                  }}
                >
                  <SearchIcon style={{ color: invertedSecondary, opacity: 0.7 }} />
                  <InputBase
                    placeholder="Search icons"
                    value={iconSearch}
                    onChange={(e) => setIconSearch(e.target.value)}
                    style={{ marginLeft: 8, flex: 1, color: invertedSecondary }}
                    inputProps={{ style: { color: invertedSecondary } }}
                  />
                </Box>

                {/* Icon grid */}
                <Grid container spacing={1} style={{ marginTop: 8 }}>
                  {(showAllIcons
                    ? iconNames.filter((n) => n.toLowerCase().includes(iconSearch.toLowerCase()))
                    : (selectedIcon
                        ? [selectedIcon, ...iconNames.filter((i) => i !== selectedIcon).slice(0, 11)]
                        : iconNames.slice(0, 12)
                      ).filter((n) => n.toLowerCase().includes(iconSearch.toLowerCase()))
                  ).map((icon) => {
                    const isSelected = icon === selectedIcon;
                    return (
                      <Grid key={icon} item xs={2}>
                        <Button
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => setSelectedIcon(icon)}
                          fullWidth
                          style={{
                            minWidth: 0,
                            padding: 6,
                            backgroundColor: isSelected ? channelStyle.mainColor : "transparent",
                            color: isSelected ? channelStyle.secondaryColor : channelStyle.mainColor,
                            borderColor: channelStyle.mainColor,
                          }}
                        >
                          <i className="material-icons">{icon}</i>
                        </Button>
                      </Grid>
                    );
                  })}
                </Grid>

                {!iconSearch && iconNames.length > 12 && (
                  <Box textAlign="center" mt={1}>
                    <Button
                      size="small"
                      onClick={() => setShowAllIcons((s) => !s)}
                      style={{ color: channelStyle.mainColor }}
                    >
                      {showAllIcons ? "Show Less" : "Show All"}
                    </Button>
                  </Box>
                )}
              </Box>
            </Popover>
          </Box>

          {/* FORM */}
          <TextField
            label="Channel Name"
            fullWidth
            margin="normal"
            value={channelName}
            onChange={({ target: { value } }) => setChannelName(value)}
            InputLabelProps={{ style: { color: invertedSecondary } }}
            InputProps={{ style: { color: invertedSecondary } }}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            multiline
            rows={4}
            value={description}
            onChange={({ target: { value } }) => setDescription(value)}
            InputLabelProps={{ style: { color: invertedSecondary } }}
            InputProps={{ style: { color: invertedSecondary } }}
          />

          {/* Publish */}
          <FormControlLabel
            control={
              <Switch
                checked={publish}
                onChange={({ target: { checked } }) => setPublish(checked)}
                style={{ color: channelStyle.mainColor }}
              />
            }
            label={<span style={{ color: invertedSecondary }}>Publish</span>}
          />

          {/* Private Channel */}
          <FormControlLabel
            control={
              <Switch
                checked={isPrivate}
                onChange={({ target: { checked } }) => setIsPrivate(checked)}
                style={{ color: channelStyle.mainColor }}
              />
            }
            label={<span style={{ color: invertedSecondary }}>Private Channel</span>}
          />
          <Typography variant="caption" style={{ color: `${invertedSecondary}99`, display: 'block', marginTop: -8, marginBottom: 8 }}>
            Private channels are only visible to members with explicit access
          </Typography>

          {/* About (click to open rich editor) */}
          <Box style={{ marginTop: 8 }}>
            <Typography style={{ color: invertedSecondary, marginBottom: 8, fontWeight: 600 }}>
              About (rich text)
            </Typography>

            <Box
              onClick={() => setAboutOpen(true)}
              style={{
                cursor: "pointer",
                border: `1px solid ${invertedSecondary}55`,
                borderRadius: 8,
                padding: 12,
                backgroundColor: `${invertedSecondary}0D`,
                color: invertedSecondary,
                position: "relative",
                maxHeight: 160,
                overflow: "hidden",
              }}
            >
              <div
                dangerouslySetInnerHTML={{ __html: aboutHtml || "<em>Click to add About…</em>" }}
                style={{ pointerEvents: "none" }}
              />
              <div
                style={{
                  position: "absolute",
                  right: 8,
                  bottom: 8,
                  fontSize: 12,
                  opacity: 0.7,
                }}
              >
                Click to edit
              </div>
            </Box>
            <Typography variant="caption" style={{ color: `${invertedSecondary}99` }}>
              You can paste formatted HTML, images, or type with rich-text shortcuts (Cmd/Ctrl+B/I/U).
            </Typography>
          </Box>

          {/* THEMES */}
          <Box mt={4}>
            <Typography variant="h6" style={{ color: invertedSecondary, marginBottom: 8 }}>
              Channel Theme
            </Typography>

            <Tabs
              value={themeTab}
              onChange={(_, v) => {
                setThemeTab(v);
                if (v === 0) {
                  if (!isPresetId(selectedThemeId)) {
                    const fallback = getThemeById("default") || PRESET_THEMES[0];
                    setSelectedThemeId(fallback.id);
                    setChannelStyle((s) => ({
                      ...s,
                      mainColor: fallback.mainColor,
                      secondaryColor: fallback.secondaryColor,
                      headerImage: fallback.headerImage,
                      fontStyle: fallback.fontStyle,
                    }));
                    setHeaderBust(0);
                  }
                } else {
                  if (isPresetId(selectedThemeId)) {
                    setSelectedThemeId("custom");
                  }
                }
              }}
              indicatorColor="primary"
              textColor="primary"
              style={{ marginBottom: 16, borderBottom: `1px solid ${invertedSecondary}33` }}
            >
              <Tab label="Default" />
              <Tab label="Custom" />
            </Tabs>

            {themeTab === 0 && (
              <Grid container spacing={2}>
                {PRESET_THEMES.map((theme) => {
                  const isSelected = selectedThemeId === theme.id;
                  const themeCardStyle = {
                    height: 80,
                    cursor: "pointer",
                    border: `2px solid ${isSelected ? channelStyle.mainColor : "transparent"}`,
                    borderRadius: 8,
                    position: "relative" as const,
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    transform: isSelected ? "scale(1.05)" : "scale(1)",
                    boxShadow: isSelected ? `0 4px 12px ${channelStyle.mainColor}33` : "0 2px 4px rgba(0,0,0,0.1)",
                  };

                  const headerUrl = resolveHeaderUrl(theme.headerImage || "");

                  return (
                    <Grid item xs={6} sm={6} style={{ flexBasis: "20%", maxWidth: "20%" }} key={theme.id}>
                      <Box
                        style={themeCardStyle}
                        onClick={() => {
                          handleThemeSelect(theme.id);
                        }}
                      >
                        <div style={{ height: "50%", background: theme.mainColor }}>
                          {headerUrl ? (
                            <img
                              src={headerUrl}
                              alt=""
                              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                          ) : null}
                        </div>
                        <div
                          style={{
                            height: "50%",
                            backgroundColor: theme.secondaryColor,
                            color: invertColor(theme.secondaryColor),
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 600,
                            padding: "0 4px",
                            textAlign: "center",
                          }}
                        >
                          {theme.name}
                        </div>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}

            {themeTab === 1 && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Main Color"
                      type="color"
                      fullWidth
                      value={channelStyle.mainColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSelectedThemeId("custom");
                        setChannelStyle((s) => ({ ...s, mainColor: v }));
                      }}
                      InputLabelProps={{ shrink: true, style: { color: invertedSecondary } }}
                      InputProps={{ style: { height: 48, padding: "8px", background: "transparent" } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Secondary Color"
                      type="color"
                      fullWidth
                      value={channelStyle.secondaryColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setSelectedThemeId("custom");
                        setChannelStyle((s) => ({ ...s, secondaryColor: v }));
                      }}
                      InputLabelProps={{ shrink: true, style: { color: invertedSecondary } }}
                      InputProps={{ style: { height: 48, padding: "8px", background: "transparent" } }}
                    />
                  </Grid>

                  {/* Header image upload + preview */}
                  <Grid item xs={12}>
                    <Box p={2} style={{ border: `1px dashed ${invertedSecondary}55`, borderRadius: 8 }}>
                      <Typography variant="body2" style={{ color: `${invertedSecondary}cc`, fontWeight: 600 }}>
                        Header Image
                      </Typography>

                      <Box mt={1} display="flex" alignItems="center" style={{ gap: 12 }}>
                        <input
                          ref={headerFileInputRef}
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={onHeaderFilePicked}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => headerFileInputRef.current?.click()}
                          disabled={headerUploading}
                          style={{ color: channelStyle.mainColor, borderColor: channelStyle.mainColor }}
                        >
                          {headerUploading ? "Uploading…" : "Choose Header Image"}
                        </Button>
                      </Box>

                      {headerUploadMsg && (
                        <Typography variant="caption" style={{ color: `${invertedSecondary}99` }}>
                          {headerUploadMsg}
                        </Typography>
                      )}

                      <Box
                        mt={2}
                        style={{ borderRadius: 8, overflow: "hidden", border: `1px solid ${invertedSecondary}33`, height: 64 }}
                      >
                        {headerImageUrl ? (
                          <img
                            src={headerImageUrl}
                            alt="header preview"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : (
                          <div style={{ width: "100%", height: "100%", background: channelStyle.mainColor }} />
                        )}
                      </Box>
                    </Box>
                  </Grid>

                  {/* LIVE PREVIEW */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ color: invertedSecondary, marginBottom: 8 }}>
                      Preview
                    </Typography>
                    <Box
                      style={{
                        borderRadius: 8,
                        overflow: "hidden",
                        boxShadow: `0 4px 12px ${invertColor(channelStyle.secondaryColor)}22`,
                        border: `1px solid ${invertedSecondary}33`,
                      }}
                    >
                      <div style={{ height: 64, background: channelStyle.mainColor }}>
                        {headerImageUrl ? (
                          <img
                            src={headerImageUrl}
                            alt="header"
                            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                          />
                        ) : null}
                      </div>
                      <Box
                        style={{
                          padding: 16,
                          background: channelStyle.secondaryColor,
                          color: invertColor(channelStyle.secondaryColor),
                        }}
                      >
                        <Typography variant="body2">Body text preview</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>

          {/* MODERATORS */}
          <Box mt={2}>
            <List component="nav">
              <ListItem button onClick={() => setModsOpen((o) => !o)}>
                <ListItemText
                  primary="Moderators & Administrators"
                  secondary={moderators.length === 0 ? "No moderators or administrators found" : `${moderators.length} moderator(s) and administrator(s)`}
                  primaryTypographyProps={{ style: { color: invertedSecondary } }}
                  secondaryTypographyProps={{ style: { color: invertedSecondary } }}
                />
                {modsOpen ? (
                  <ExpandLess style={{ color: channelStyle.mainColor }} />
                ) : (
                  <ExpandMore style={{ color: channelStyle.mainColor }} />
                )}
              </ListItem>
              <Collapse in={modsOpen} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {moderators.length > 0 && (
                    <ListItem style={{ paddingLeft: 32 }}>
                      <ListItemText
                        primary={
                          <Box display="grid" gridTemplateColumns="1fr 1fr 2fr 1fr" gridGap={8} style={{ fontWeight: 700 }}>
                            <span>First name</span>
                            <span>Last name</span>
                            <span>Email</span>
                            <span>Role</span>
                          </Box>
                        }
                        primaryTypographyProps={{ style: { color: invertedSecondary } }}
                      />
                    </ListItem>
                  )}

                  {moderators.length > 0 ? (
                    moderators.map((mod: any, index: number) => (
                      <ListItem key={index} style={{ paddingLeft: 32 }}>
                        <ListItemText
                          primary={
                            <Box display="grid" gridTemplateColumns="1fr 1fr 2fr 1fr" gridGap={8}>
                              <span>{mod.firstName || mod.name?.split(' ')[0] || '-'}</span>
                              <span>{mod.lastName || mod.name?.split(' ')[1] || '-'}</span>
                              <span>{mod.email || '-'}</span>
                              <span style={{ textTransform: 'capitalize' }}>
                                {mod.role || mod.userType || '-'}
                              </span>
                            </Box>
                          }
                          primaryTypographyProps={{ style: { color: invertedSecondary } }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <ListItem style={{ paddingLeft: 32 }}>
                      <ListItemText
                        secondary="No moderators or administrators are currently assigned to this community"
                        secondaryTypographyProps={{ style: { color: invertedSecondary, opacity: 0.8 } }}
                      />
                    </ListItem>
                  )}
                </List>
              </Collapse>
            </List>
          </Box>

          {/* NOTIFICATION POLICY */}
          <Box mt={2}>
            <List component="nav">
              <ListItem button onClick={() => setNotificationPolicyOpen((o) => !o)}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <i className="material-icons" style={{ fontSize: 20, color: channelStyle.mainColor }}>
                        notifications_active
                      </i>
                      <span>Notification Policy for This Channel</span>
                    </Box>
                  }
                  secondary="Configure default notification settings for this specific channel only"
                  primaryTypographyProps={{ style: { color: invertedSecondary } }}
                  secondaryTypographyProps={{ style: { color: invertedSecondary, opacity: 0.8 } }}
                />
                {notificationPolicyOpen ? (
                  <ExpandLess style={{ color: channelStyle.mainColor }} />
                ) : (
                  <ExpandMore style={{ color: channelStyle.mainColor }} />
                )}
              </ListItem>
              <Collapse in={notificationPolicyOpen} timeout="auto" unmountOnExit>
                <Box style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}>
                  <NotificationPolicyEditor
                    communityIdentifier={communityIdentifier}
                    channelIdentifier={channelIdentifier}
                  />
                </Box>
              </Collapse>
            </List>
          </Box>

          {/* CHATROOMS */}
          <Box mt={2}>
            <List component="nav">
              <ListItem button onClick={() => setChatroomsOpen((o) => !o)}>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <i className="material-icons" style={{ fontSize: 20, color: channelStyle.mainColor }}>
                        chat
                      </i>
                      <span>Chatrooms</span>
                    </Box>
                  }
                  secondary="Manage real-time chat rooms for this channel"
                  primaryTypographyProps={{ style: { color: invertedSecondary } }}
                  secondaryTypographyProps={{ style: { color: invertedSecondary, opacity: 0.8 } }}
                />
                {chatroomsOpen ? (
                  <ExpandLess style={{ color: channelStyle.mainColor }} />
                ) : (
                  <ExpandMore style={{ color: channelStyle.mainColor }} />
                )}
              </ListItem>
              <Collapse in={chatroomsOpen} timeout="auto" unmountOnExit>
                <Box style={{ paddingLeft: 16, paddingRight: 16, paddingBottom: 16 }}>
                  <AdminChatroomManager
                    programId={programId}
                    communityIdentifier={communityIdentifier}
                    channelIdentifier={channelIdentifier}
                  />
                </Box>
              </Collapse>
            </List>
          </Box>

          {/* SAVE */}
          {saveError && (
            <Typography variant="body2" style={{ color: "#d32f2f", marginTop: 8 }}>
              {saveError}
            </Typography>
          )}
          <Box mt={4}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              style={{ backgroundColor: channelStyle.mainColor, color: channelStyle.secondaryColor }}
            >
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </Box>
        </MuiCard>
      </div>
    </>
  );
}
